import { useCallback, useEffect, useRef, useState } from 'react';
import { useStaticCallback } from './useStaticCallback';

type Task = () => void;

export interface UseDelayConfig {
  runOnMount?: boolean;
}

/**
 * Executes a task after a delay.
 *
 * The hook returns a tuple `[execute, cancel]` where `execute` will begin the delay
 * after which the task will be executed and `cancel` will cancel the pending task
 * if it has not yet been executed.
 *
 * If the delay timer should begin on mount, the `runOnMount` option can be set to
 * `true`.
 */
export default function useDelay(
  task: Task,
  delayMillis: number,
  config?: UseDelayConfig,
) {
  const { runOnMount = false } = config ?? {};

  const timerIdRef = useRef<number>();
  const staticTask = useStaticCallback(task);
  const [initialRunOnMount] = useState(runOnMount);

  const cancel = useCallback(() => {
    if (timerIdRef.current) {
      window.clearTimeout(timerIdRef.current);
      timerIdRef.current = undefined;
    }
  }, []);

  const execute = useCallback(() => {
    cancel();
    timerIdRef.current = window.setTimeout(staticTask, delayMillis);
  }, [cancel, delayMillis, staticTask]);

  useEffect(() => {
    if (initialRunOnMount) {
      execute();
    }
  }, [execute, initialRunOnMount]);

  // cancel taks on unmount
  useEffect(
    () => () => {
      cancel();
    },
    [cancel],
  );

  return [execute, cancel];
}
